
import { map as rxjsMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { DatePipe } from '@angular/common';
import { without, reduce, each, map } from 'lodash';

import { settings } from '../../app.settings';
import { OfferMixed } from '../../shared/models/offer-mixed';
import { Offer, toApi } from '../../shared/models/offer';
import { BaseService } from '~common/app/shared/services/base.service';
import { CouponGeneric } from '../../shared/models/coupon-generic';
import { CouponUnique } from '../../shared/models/coupon-unique';
import { NaiveParameterCodec } from '~common/app/shared/utils/encoding';

const API_PATH = settings.api.url + '/offers';
const API_PATH_METRICS = settings.api.urlPerks + '/metrics/';
const API_PATH_PROGRAM = settings.api.url + '/programs/me/offers';

export class OfferFlow {
  type: string;
  recipient: string;
  selection = {};
}

@Injectable()
export class OfferService extends BaseService {
  flow = new OfferFlow;

  constructor(
    private http: HttpClient,
    private datePipe: DatePipe,
  ) {
    super();
  }

  extractFlowRecipientName() {
    const recipient = this.flow.recipient;
    const selection = this.flow.selection;

    if (!selection[recipient]) {
      return '';
    }

    if (selection[recipient].length > 1) {
      return 'shared.label.multiple';
    }
    return selection[recipient][0].name;
  }

  extractFlowRecipientIds() {
    const payload: any = {};
    const recipient = this.flow.recipient;
    const selection = this.flow.selection;

    if (!selection[recipient]) {
      return payload;
    }

    switch (this.flow.recipient) {
      case 'STORE':
        payload.stores = map(selection[recipient], 'id');
        break;
      case 'BRAND':
        payload.brand = map(selection[recipient], 'id')[0];
        break;
      case 'NETWORK':
        payload.network = map(selection[recipient], 'id')[0];
        break;
    }

    return payload;
  }

  extractFlowRecipientSelection() {
    const payload: any = {};
    const recipient = this.flow.recipient;
    const selection = this.flow.selection;

    if (!selection[recipient]) {
      // return this.router.navigate([ 'offers/recipients' ]);
      return payload;
    }

    switch (this.flow.recipient) {
      case 'STORE':
        payload.stores = selection[recipient];
        break;
      case 'BRAND':
        payload.brand = selection[recipient][0];
        break;
      case 'NETWORK':
        payload.network = selection[recipient][0];
        break;
    }

    return payload;
  }

  findUniqueRecipientBrand(offer) {
    if (!offer || !offer.stores.length) {
      return false;
    }

    const brand = reduce(offer.stores, function (acc, store) {
      if (acc && store.brand && acc.id === store.brand.id) {
        return store.brand;
      } else {
        return false;
      }
    }, offer.stores[0].brand);

    if (brand) {
      return brand;
    }

    return false;
  }

  computeRecipientName(offer) {
    if ((offer.stores || []).length) {
      if (offer.stores.length === 1) {
        return offer.stores[0].name;
      } else {
        const brand = this.findUniqueRecipientBrand(offer);
        if (brand) {
          return brand.name;
        }

        return 'shared.label.multiple';
      }
    }

    if (offer.network) {
      return offer.network.name;
    }

    if (offer.brand) {
      return offer.brand.name;
    }

    return this.extractFlowRecipientName();
  }

  fetch(params = {}): Observable<any> {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });

    each(params, (value: any, key: any) => {
      if (key === 'status' && value) {
        if (!Array.isArray(value)) {
          value = [value];
        }
        value.forEach((item: string) => {
          apiParams = apiParams.append('status', item);
        });
      } else if (key === 'filters') {
        if (value.term) {
          apiParams = apiParams.append('keyword', value.term);
        }

        if (value.start_date) {
          apiParams = apiParams.append('end_date', this.datePipe.transform(value.start_date, 'yyyy-MM-ddT00:00:00') + '--');
        }

        if (value.end_date) {
          apiParams = apiParams.append('start_date', '--' + this.datePipe.transform(value.end_date, 'yyyy-MM-ddT23:59:59'));
        }
      } else if (value || value === 0) {
        apiParams = apiParams.append(key, value);
      }
    });

    return this.http.get(API_PATH_PROGRAM, {
      params: apiParams
    }).pipe(
      rxjsMap((response) => {
        return this.hydrate(OfferMixed, response);
      })
    );
  }

  fetchCounters(): Observable<any> {
    const apiParams = new HttpParams();
    // apiParams = apiParams.append('type', 'CASHBACK');

    return this.http.get(API_PATH_PROGRAM + '/metrics/status', {
      params: apiParams
    });
  }

  fetchHistory(params = {}): Observable<any> {
    return this.fetch(params);
  }

  // Cashback offers

  get(id): Observable<any> {
    return this.http.get(API_PATH_PROGRAM + '/' + id).pipe(rxjsMap(response => {
      return this.hydrate(OfferMixed, response);
    }));
  }

  create(data): Observable<any> {
    return this.http.post(API_PATH_PROGRAM + '/cashback', toApi(data));
  }

  update(id, data): Observable<any> {
    return this.http.put(API_PATH_PROGRAM + '/' + id, toApi(data));
  }

  save(data) {
    if (data.id) {
      return this.update(data.id, data);
    } else {
      return this.create(data);
    }
  }

  // Unique coupon offer

  getUniqueCoupon(id): Observable<any> {
    return this.http.get(API_PATH_PROGRAM + '/' + id).pipe(rxjsMap(response => {
      return this.hydrate(CouponUnique, response);
    }));
  }

  createUniqueCoupon(data): Observable<any> {
    return this.http.post(API_PATH_PROGRAM + '/coupon/unique', toApi(data));
  }

  updateUniqueCoupon(id, data): Observable<any> {
    return this.http.put(API_PATH_PROGRAM + '/' + id, toApi(data));
  }

  saveUniqueCoupon(data) {
    if (data.id) {
      return this.updateUniqueCoupon(data.id, data);
    } else {
      return this.createUniqueCoupon(data);
    }
  }

  // Generic coupon offer

  getGenericCoupon(id): Observable<any> {
    return this.http.get(API_PATH_PROGRAM + '/' + id).pipe(rxjsMap(response => {
      return this.hydrate(CouponGeneric, response);
    }));
  }

  createGenericCoupon(data): Observable<any> {
    return this.http.post(API_PATH_PROGRAM + '/coupon/generic', toApi(data));
  }

  updateGenericCoupon(id, data): Observable<any> {
    return this.http.put(API_PATH_PROGRAM + '/' + id, toApi(data));
  }

  saveGenericCoupon(data) {
    if (data.id) {
      return this.updateGenericCoupon(data.id, data);
    } else {
      return this.createGenericCoupon(data);
    }
  }

  activateOffer(id) {
    return this.http.put(API_PATH_PROGRAM + '/' + id + '/activate', {});
  }

  deactivateOffer(id) {
    return this.http.put(API_PATH_PROGRAM + '/' + id + '/deactivate', {});
  }

  // Metrics

  getMetrics(id, granularity = 'monthly', year = null, month = null): Observable<any> {
    const params = without([granularity, year, month], null).join('/');

    return this.http.get(API_PATH_PROGRAM + '/' + id + '/metrics/' + params).pipe(rxjsMap(response => {
      return response;
    }));
  }

  getMetricsExport(id, granularity = 'monthly', year = null, month = null) {
    const params = without([granularity, year, month, 'data.csv'], null).join('/');

    return this.http.get(API_PATH_PROGRAM + '/' + id + '/metrics/' + params, { responseType: 'text' });
  }

  /**
   * The Performances-tab metrics restricted to one offer: same endpoints and same payload shape as
   * the program-wide dashboard, minus the per-brand leaderboards, which hold a single bar once the
   * query is scoped to an offer. `params.program_offer_id` is what does the scoping.
   */
  getDashboardMetrics(type, params = {}): Observable<any> {
    switch (type) {
      case 'REWARDS':
        return forkJoin([
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'rewarded/count'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'playground/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'fees/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'revenues/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'rewards/count'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'rewards/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'rewards/average-amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'revenues/average-basket/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'fees/timeseries'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'acb', 'rewarded/timeseries'), params)
        ]).pipe(
          rxjsMap(([rewardedCount, playgroundAmount, feesAmount, revenueAmount, rewardCount, rewardAmount, rewardAverageAmount, averageBasket, feesTimeseries, rewardedTimeseries]: any[]) => ({
            rewardedCount: rewardedCount?.count || 0,
            playgroundAmount: playgroundAmount?.count || 0,
            feesAmount: feesAmount?.count || 0,
            revenueAmount: revenueAmount?.count || 0,
            rewardCount: rewardCount?.count || 0,
            rewardAmount: rewardAmount?.count || 0,
            rewardAverageAmount: rewardAverageAmount?.count || 0,
            averageBasket: averageBasket?.count || 0,
            feesTimeseries,
            rewardedTimeseries
          }))
        );

      case 'VOUCHERS':
        return forkJoin([
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'vouchers/count'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'vouchers/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'vouchers/sell/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'discount/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'fees/amount'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'discount/timeseries'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'consumers/count/timeseries'), params),
          this.fetchMetric(this.offerMetricUrl(params, 'evo', 'vouchers/count/timeseries'), params)
        ]).pipe(
          rxjsMap(([vouchersCount, vouchersAmount, vouchersSellAmount, discountAmount, feesAmount, discountTimeseries, consumersCountTimeseries, vouchersCountTimeseries]: any[]) => ({
            vouchersCount: vouchersCount?.count || 0,
            vouchersAmount: vouchersAmount?.count || 0,
            vouchersSellAmount: vouchersSellAmount?.count || 0,
            discountAmount: discountAmount?.count || 0,
            feesAmount: feesAmount?.count || 0,
            discountTimeseries,
            consumersCountTimeseries,
            vouchersCountTimeseries
          }))
        );
    }
  }

  getDashboardMetricsExportUrl(type, params: any = {}): string {
    const scope = type === 'VOUCHERS' ? 'evo' : 'acb';
    return `${this.offerMetricUrl(params, scope, 'export.csv')}?${this.metricParams(params).toString()}`;
  }

  // The offer is a path segment, so the route itself says what it is scoped to and the API can
  // reject an offer the program does not own before any metric is computed.
  private offerMetricUrl(params: any, scope: string, metric: string): string {
    return `${API_PATH_METRICS}${scope}/offers/${params.program_offer_id}/${metric}`;
  }

  private metricParams(params: any): HttpParams {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });
    if (params.start_date) {
      apiParams = apiParams.append('start_date', params.start_date);
    }
    apiParams = apiParams.append('end_date', params.end_date);
    apiParams = apiParams.append('group_by', params.group_by);
    return apiParams;
  }

  // Named apart from the public fetch() above, which lists offers on a different API.
  private fetchMetric(url, params: any = {}): Observable<any> {
    return this.http.get(url, { params: this.metricParams(params) });
  }

  autocomplete(query: string, limit = 100) {
    let apiParams = new HttpParams({ encoder: new NaiveParameterCodec() });
    apiParams = apiParams.append('query', query);
    apiParams = apiParams.append('limit', '' + limit);

    return this.http.get(API_PATH_PROGRAM + '/search', {
      params: apiParams
    }).pipe(rxjsMap((response: any) => {
      return response.map((item) => {
        return {
          id: item.id,
          name: item.reference
        };
      });
    }));
  }

  // Phase

  fetchPhases(offerId: string) {
    return this.http.get(API_PATH_PROGRAM + '/' + offerId + '/phases');
  }

  deletePhase(offerId: string, phaseId: string) {
    return this.http.delete(API_PATH_PROGRAM + '/' + offerId + '/phases/' + phaseId);
  }

  savePhase(offerId: string, data) {
    if (data.start_date) {
      data.start_date = new Date(data.start_date).toISOString().substring(0, 10)
    }

    if (data.end_date) {
      data.end_date = new Date(data.end_date).toISOString().substring(0, 10)
    }
    if (data.id) {
      const id = data.id
      Reflect.deleteProperty(data, 'id')
      return this.http.put(API_PATH_PROGRAM + '/' + offerId + '/phases/' + id, data);
    } else {
      return this.http.post(API_PATH_PROGRAM + '/' + offerId + '/phases', data);
    }
  }
}
